import { PlusOutlined } from "@ant-design/icons"
import { Button } from "components/Button/Button"
import { Checkbox } from "components/Checkbox/Checkbox"
import { ErrorStatus } from "components/ErrorStatus/ErrorStatus"
import { useNotification } from "components/Notification/Notification"
import { Radio } from "components/Radio/Radio"
import { Spinner } from "components/Spinner/Spinner"
import {
	AdminCareOrganizationProvider,
	AdminCareOrganizationProviderPermissionPayload,
} from "interface/careOrganization"
import { useCallback, useMemo, useState } from "react"
import {
	useAdminCareOrganizationProviderAdd,
	useAdminCareOrganizationProviderPermissionUpdate,
	useAdminCareOrganizationProviders,
} from "services/organizations"
import { GENERIC_ERROR_MESSAGE } from "utils/constants"
import { type ProviderOption, SearchProvider } from "./SearchProvider"

export function CareOrganizationProviders({
	careOrganizationId,
}: {
	careOrganizationId: string
}) {
	const {
		data: providers = [],
		isPending,
		isError,
		error,
	} = useAdminCareOrganizationProviders(careOrganizationId)
	const linkedProviderIds = useMemo(
		() => providers.map(({ id }) => id),
		[providers],
	)

	return (
		<section
			aria-labelledby="care-organization-providers"
			className="flex flex-col gap-3 rounded-2xl bg-mint-0 p-2 xs:p-4"
		>
			<h3 id="care-organization-providers" className="text-lg font-semibold">
				Providers
			</h3>
			<AddProvider
				careOrganizationId={careOrganizationId}
				linkedProviderIds={linkedProviderIds}
			/>
			{isPending ? (
				<Spinner className="py-4" />
			) : isError ? (
				<ErrorStatus show>
					{error?.message ?? GENERIC_ERROR_MESSAGE}
				</ErrorStatus>
			) : providers.length === 0 ? (
				<p className="text-navy-60">No providers linked yet</p>
			) : (
				<ul
					aria-label={`${providers.length} provider(s)`}
					className="divide-y divide-dashed divide-grey-20 rounded-lg border border-grey-20 p-2"
				>
					{providers.map((provider) => (
						<ProviderPermissions
							key={provider.id}
							careOrganizationId={careOrganizationId}
							provider={provider}
						/>
					))}
				</ul>
			)}
		</section>
	)
}

function AddProvider({
	careOrganizationId,
	linkedProviderIds,
}: {
	careOrganizationId: string
	linkedProviderIds: string[]
}) {
	const { notify } = useNotification()
	const add = useAdminCareOrganizationProviderAdd(careOrganizationId)
	const [selected, setSelected] = useState<ProviderOption>()
	const handleAdd = useCallback(() => {
		if (!selected) return

		add.mutate(
			{ userId: selected.value },
			{
				onSuccess: () => {
					notify.success({
						message: "Add provider",
						description: `${selected.label} was added`,
					})
					setSelected(undefined)
				},
			},
		)
	}, [add, notify, selected])

	return (
		<div className="flex flex-col gap-2">
			<div className="flex gap-2">
				<SearchProvider
					className="min-w-0 flex-1"
					value={selected?.value ?? null}
					linkedProviderIds={linkedProviderIds}
					disabled={add.isPending}
					onChange={(_, option) => {
						add.reset()
						setSelected(
							Array.isArray(option) ? option[0] : (option ?? undefined),
						)
					}}
				/>
				<Button
					type="primary"
					icon={<PlusOutlined aria-hidden />}
					disabled={!selected}
					loading={add.isPending}
					onClick={handleAdd}
				>
					Add provider
				</Button>
			</div>
			<ErrorStatus show={add.isError}>
				{add.error?.message ?? GENERIC_ERROR_MESSAGE}
			</ErrorStatus>
		</div>
	)
}

function ProviderPermissions({
	careOrganizationId,
	provider,
}: {
	careOrganizationId: string
	provider: AdminCareOrganizationProvider
}) {
	const { notify } = useNotification()
	const update = useAdminCareOrganizationProviderPermissionUpdate(
		careOrganizationId,
		provider.id,
	)
	const saved: AdminCareOrganizationProviderPermissionPayload = {
		communicationPermission: provider.communicationPermission ?? "none",
		canEnrollPatients: provider.canEnrollPatients,
		canManageNurseTimes: provider.canManageNurseTimes,
	}
	// Show the pending change; on failure this falls back to the saved values
	const permissions = update.isPending ? update.variables : saved
	const save = (
		change: Partial<AdminCareOrganizationProviderPermissionPayload>,
	) =>
		update.mutate(
			// Always send all three, as the API resets any flag left out
			{ ...saved, ...change },
			{
				onSuccess: () => {
					notify.success({
						message: "Provider permissions",
						description: `Permissions updated for ${provider.fullName}`,
					})
				},
			},
		)

	return (
		<li
			aria-label={provider.fullName}
			className="flex flex-col gap-1 not-first:pt-2 not-last:pb-3"
		>
			<dl className="grid grid-cols-[auto_1fr] gap-x-3 text-sm">
				<dt className="text-navy-60">Name</dt>
				<dd className="font-semibold">{provider.fullName}</dd>
				<dt className="text-navy-60">Email</dt>
				<dd className="break-all">{provider.email}</dd>
				<dt className="text-navy-60">User ID</dt>
				<dd className="break-all">{provider.id}</dd>
			</dl>
			<div className="flex flex-wrap items-center gap-2">
				<span id={`communication-${provider.id}`}>
					Communication permission
				</span>
				<Radio.Group
					aria-labelledby={`communication-${provider.id}`}
					optionType="button"
					size="small"
					disabled={update.isPending}
					value={permissions.communicationPermission}
					options={COMMUNICATION_OPTIONS}
					onChange={({ target: { value } }) =>
						save({ communicationPermission: value })
					}
				/>
			</div>
			<div className="flex flex-wrap gap-4">
				<Checkbox
					disabled={update.isPending}
					checked={permissions.canEnrollPatients}
					onChange={({ target: { checked } }) =>
						save({ canEnrollPatients: checked })
					}
				>
					Can enroll patients?
				</Checkbox>
				<Checkbox
					disabled={update.isPending}
					checked={permissions.canManageNurseTimes}
					onChange={({ target: { checked } }) =>
						save({ canManageNurseTimes: checked })
					}
				>
					Can manage nurse times?
				</Checkbox>
			</div>
			<ErrorStatus show={update.isError}>
				{update.error?.message ?? GENERIC_ERROR_MESSAGE}
			</ErrorStatus>
		</li>
	)
}

const COMMUNICATION_OPTIONS: {
	label: string
	value: AdminCareOrganizationProviderPermissionPayload["communicationPermission"]
}[] = [
	{ label: "None", value: "none" },
	{ label: "View", value: "view" },
	{ label: "Send", value: "send" },
]
